import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Mail, Phone, MapPin, Calendar, Briefcase } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, Loading, Badge, Avatar, fmtDate } from "../components/ui";

const SECTIONS = [
  ["Personal", [["Salutation", "salutation"], ["Gender", "gender"], ["Date of Birth", "dob", true], ["Blood Group", "bloodGroup"], ["Nationality", "nationality"], ["Marital Status", "maritalStatus"]]],
  ["Contact", [["Email", "email"], ["Personal Email", "personalEmail"], ["Phone", "phone"], ["Personal Mobile", "personalMobile"]]],
  ["Employment", [["Employee Code", "employeeCode"], ["Job Title", "jobTitle"], ["Employment Type", "employmentType"], ["Join Date", "joinDate", true], ["Total Experience", "totalExperience"]]],
  ["Identity & Documents", [["PAN", "panNumber"], ["Aadhaar", "aadhaarNumber"], ["Passport", "passportNumber"], ["UAN", "uanNumber"]]],
  ["Statutory", [["PF Number", "pfNumber"], ["ESI Number", "esiNumber"], ["PT State", "ptState"]]],
  ["Address", [["Address", "addressLine"], ["City", "city"], ["State", "state"], ["Country", "country"], ["Pincode", "pincode"]]],
  ["Emergency Contact", [["Name", "emergencyName"], ["Relation", "emergencyRelation"], ["Mobile", "emergencyMobile"]]],
  ["Bank & Salary", [["Bank", "bankName"], ["Account Number", "accountNumber"], ["IFSC/SWIFT", "swiftIfscCode"]]],
  ["Education & Skills", [["Degree", "educationDegree"], ["Specialisation", "fieldOfSpecialisation"], ["University", "university"], ["Skills", "skills"]]],
];

export default function EmployeeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [e, setE] = useState(null);
  const canEdit = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);

  useEffect(() => { api.get(`/employees/${id}`).then(({ data }) => setE(data)).catch(() => nav("/employees")); }, [id, nav]);
  if (!e) return <Loading />;

  const val = (k, isDate) => { const v = e[k]; if (v == null || v === "") return "—"; return isDate ? fmtDate(v) : String(v); };

  return (
    <div>
      <Link to="/employees" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-accent"><ArrowLeft size={15} /> Back to employees</Link>

      <Card className="mb-6 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-800 dark:to-slate-900" />
        <div className="flex flex-wrap items-end justify-between gap-4 px-6 pb-6">
          <div className="flex items-end gap-4">
            <div className="-mt-10 rounded-full ring-4 ring-white dark:ring-slate-900"><Avatar name={e.fullName} size={80} url={e.profilePhotoUrl} /></div>
            <div className="pb-1">
              <h1 className="font-head text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{e.fullName}</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">{e.designationName} · {e.departmentName}</p>
              <div className="mt-2 flex flex-wrap gap-2"><Badge value={e.status} /><span className="font-mono text-xs text-slate-400">{e.employeeCode}</span></div>
            </div>
          </div>
          {canEdit && <Link to={`/employees/${id}/edit`} data-testid="edit-employee" className="btn-outline"><Pencil size={14} /> Edit</Link>}
        </div>
      </Card>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        {[[Mail, "Email", e.email], [Phone, "Phone", e.phone || "—"], [MapPin, "Location", e.locationName || "—"], [Calendar, "Joined", fmtDate(e.joinDate)]].map(([Ic, l, v]) => (
          <Card key={l} className="p-4"><div className="flex items-center gap-2 text-slate-400"><Ic size={15} /><span className="lbl">{l}</span></div><p className="mt-2 truncate text-sm font-medium text-slate-800 dark:text-slate-100">{v}</p></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {SECTIONS.map(([title, fields]) => (
          <Card key={title} className="p-6" data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <h3 className="mb-4 flex items-center gap-2 font-head text-base font-bold text-slate-900 dark:text-white"><Briefcase size={15} className="text-accent" />{title}</h3>
            <dl className="space-y-3">
              {fields.map(([label, key, isDate]) => (
                <div key={key} className="flex items-start justify-between gap-4">
                  <dt className="text-sm text-slate-500 dark:text-slate-400">{label}</dt>
                  <dd className="text-right text-sm font-medium text-slate-800 dark:text-slate-100">{val(key, isDate)}</dd>
                </div>
              ))}
            </dl>
          </Card>
        ))}
      </div>
    </div>
  );
}
